import axios from 'axios';
import { getMarketConfig } from '../config/markets';

const API_KEY = process.env.REACT_APP_AIRTABLE_API_KEY;

// Mutable default base ID — initialized from env-mode config, updated by MarketProvider in path-mode
let _defaultBaseId = getMarketConfig().airtableBaseId || process.env.REACT_APP_AIRTABLE_BASE_ID;

/**
 * Set the default Airtable base ID at runtime.
 * Called by MarketProvider when the active market changes (path-mode).
 */
export function setDefaultBaseId(baseId) {
  _defaultBaseId = baseId;
}

// =====================================================================
// In-memory cache — avoids redundant Airtable fetches on market switch
// =====================================================================
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const _cache = new Map(); // key: "baseId::tableName" → { data, timestamp, promise }

function cacheKey(baseId, tableName) {
  return `${baseId}::${tableName}`;
}

/**
 * Clear all cached data. Called on manual pull-to-refresh so users
 * can force-fetch fresh data when they want it.
 */
export function clearAirtableCache() {
  _cache.clear();
}

// Helper: build auth headers
const authHeaders = () => ({ Authorization: `Bearer ${API_KEY}` });

// Helper: build base URL for a given base ID
const baseUrl = (baseId) => `https://api.airtable.com/v0/${baseId}`;

// Helper function to fetch all records with pagination (for a specific base), uncached
const _fetchAllRecordsUncached = async (baseId, tableName) => {
  let allRecords = [];
  let offset = null;

  do {
    const params = offset ? { offset } : {};
    const response = await axios.get(
      `${baseUrl(baseId)}/${encodeURIComponent(tableName)}`,
      { params, headers: authHeaders() }
    );
    allRecords = allRecords.concat(response.data.records);
    offset = response.data.offset;
  } while (offset);

  return allRecords;
};

/**
 * Fetch all records with caching + request deduplication.
 * - Returns cached data if it exists and is within TTL.
 * - If a fetch is already in-flight for the same key, returns the same promise
 *   (prevents duplicate requests on rapid clicks or StrictMode double-renders).
 * - Otherwise fetches fresh data and caches it.
 */
const fetchAllRecordsForBase = async (baseId, tableName) => {
  const key = cacheKey(baseId, tableName);
  const cached = _cache.get(key);

  // Return cached data if fresh
  if (cached && cached.data && (Date.now() - cached.timestamp < CACHE_TTL_MS)) {
    return cached.data;
  }

  // Return in-flight promise if one exists (request deduplication)
  if (cached && cached.promise) {
    return cached.promise;
  }

  // Fetch fresh data
  const promise = _fetchAllRecordsUncached(baseId, tableName)
    .then(data => {
      _cache.set(key, { data, timestamp: Date.now(), promise: null });
      return data;
    })
    .catch(err => {
      // Clear the failed promise so retries work
      _cache.delete(key);
      throw err;
    });

  // Store the in-flight promise for deduplication
  _cache.set(key, { ...(cached || {}), promise });

  return promise;
};

// Helper function to fetch all records with pagination (default base)
const fetchAllRecords = async (tableName) => {
  return fetchAllRecordsForBase(_defaultBaseId, tableName);
};

// Helper to patch a record on the default base
const patchRecord = async (tablePath, fields) => {
  const response = await axios.patch(
    `${baseUrl(_defaultBaseId)}/${tablePath}`,
    { fields },
    { headers: authHeaders() }
  );
  return response.data;
};

// Fetch all restaurants - visibility controlled by whether they have content
export const getRestaurants = async () => {
  try {
    return await fetchAllRecords('Restaurants');
  } catch (error) {
    console.error('Error fetching restaurants:', error);
    throw error;
  }
};

// Fetch all happy hour deals
export const getHappyHourDeals = async () => {
  try {
    return await fetchAllRecords('Happy Hour Deals');
  } catch (error) {
    console.error('Error fetching happy hour deals:', error);
    throw error;
  }
};

// Fetch all restaurant specials
export const getRestaurantSpecials = async () => {
  try {
    return await fetchAllRecords('Restaurant Specials');
  } catch (error) {
    console.error('Error fetching restaurant specials:', error);
    throw error;
  }
};

// Fetch all events
export const getEvents = async () => {
  try {
    return await fetchAllRecords('Events');
  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
};

// Fetch settings (for feature flags like carousel toggle)
export const getSettings = async () => {
  try {
    return await fetchAllRecords('Settings');
  } catch (error) {
    // Settings table may not exist yet - return empty array
    console.warn('Settings table not found or error fetching:', error.message);
    return [];
  }
};

// Update a single setting record by ID
export const updateSetting = async (recordId, fields) => {
  try {
    return await patchRecord(`Settings/${recordId}`, fields);
  } catch (error) {
    console.error('Error updating setting:', error);
    throw error;
  }
};

// Fetch aggregator sources (for the aggregator scraper dropdown)
export const getAggregatorSources = async () => {
  try {
    const records = await fetchAllRecords('Aggregator Sources');
    // Filter to only active sources and sort by name
    return records
      .filter(r => r.fields.Active)
      .sort((a, b) => (a.fields.Name || '').localeCompare(b.fields.Name || ''));
  } catch (error) {
    console.warn('Aggregator Sources table not found or error fetching:', error.message);
    return [];
  }
};

// Update an aggregator source record (for updating Last Scraped, etc.)
export const updateAggregatorSource = async (recordId, fields) => {
  try {
    return await patchRecord(`Aggregator Sources/${recordId}`, fields);
  } catch (error) {
    console.error('Error updating aggregator source:', error);
    throw error;
  }
};

// Fetch advertisements
export const getAdvertisements = async () => {
  try {
    return await fetchAllRecords('Advertising');
  } catch (error) {
    console.warn('Advertising table not found or error fetching:', error.message);
    return [];
  }
};

// Track ad click - increment Click_Count
export const trackAdClick = async (recordId, currentCount) => {
  try {
    await patchRecord(`Advertising/${recordId}`, { Click_Count: (currentCount || 0) + 1 });
  } catch (error) {
    console.error('Error tracking ad click:', error);
  }
};

// Track ad impression - increment Impression_Count
export const trackAdImpression = async (recordId, currentCount) => {
  try {
    await patchRecord(`Advertising/${recordId}`, { Impression_Count: (currentCount || 0) + 1 });
  } catch (error) {
    console.error('Error tracking ad impression:', error);
  }
};

// =====================================================================
// Base-aware versions (for Admin Console market switcher)
// =====================================================================

export const getRestaurantsForBase = async (baseId) => {
  try {
    return await fetchAllRecordsForBase(baseId, 'Restaurants');
  } catch (error) {
    console.error('Error fetching restaurants for base:', error);
    throw error;
  }
};

export const getSettingsForBase = async (baseId) => {
  try {
    return await fetchAllRecordsForBase(baseId, 'Settings');
  } catch (error) {
    console.warn('Settings table not found for base:', error.message);
    return [];
  }
};

export const getAggregatorSourcesForBase = async (baseId) => {
  try {
    const records = await fetchAllRecordsForBase(baseId, 'Aggregator Sources');
    return records
      .filter(r => r.fields.Active)
      .sort((a, b) => (a.fields.Name || '').localeCompare(b.fields.Name || ''));
  } catch (error) {
    console.warn('Aggregator Sources table not found for base:', error.message);
    return [];
  }
};

export const updateSettingForBase = async (baseId, recordId, fields) => {
  try {
    const response = await axios.patch(
      `${baseUrl(baseId)}/Settings/${recordId}`,
      { fields },
      { headers: authHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating setting for base:', error);
    throw error;
  }
};

export const createRecordForBase = async (baseId, tableName, fields) => {
  try {
    const response = await axios.post(
      `${baseUrl(baseId)}/${encodeURIComponent(tableName)}`,
      { fields },
      { headers: authHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating record for base:', error);
    throw error;
  }
};

// Fetch custom keyword records from Settings (Setting Name: custom_food_keywords / custom_drink_keywords)
export const getCustomKeywordsForBase = async (baseId) => {
  try {
    const settings = await fetchAllRecordsForBase(baseId, 'Settings');
    const foodRecord = settings.find(s => s.fields['Setting Name'] === 'custom_food_keywords');
    const drinkRecord = settings.find(s => s.fields['Setting Name'] === 'custom_drink_keywords');
    const parseKeywords = (rec) =>
      rec && rec.fields.Value ? rec.fields.Value.split(',').map(k => k.trim()).filter(Boolean) : [];
    return {
      food: parseKeywords(foodRecord),
      foodRecordId: foodRecord ? foodRecord.id : null,
      drink: parseKeywords(drinkRecord),
      drinkRecordId: drinkRecord ? drinkRecord.id : null,
    };
  } catch (error) {
    console.warn('Could not fetch custom keywords:', error.message);
    return { food: [], foodRecordId: null, drink: [], drinkRecordId: null };
  }
};

// Save custom keywords back to Airtable (creates record if none exists)
export const saveCustomKeywordsForBase = async (baseId, type, keywords, recordId) => {
  const settingName = type === 'food' ? 'custom_food_keywords' : 'custom_drink_keywords';
  const value = keywords.join(', ');
  try {
    if (recordId) {
      return await updateSettingForBase(baseId, recordId, { Value: value });
    }
    return await createRecordForBase(baseId, 'Settings', { 'Setting Name': settingName, Value: value });
  } catch (error) {
    const detail = error.response?.data?.error?.message || error.response?.data?.error || error.message;
    throw new Error(`${detail}`);
  }
};
